/**
 * Modal for cropping a photo.
 * Uses react-image-crop for the crop UI. By default it crops in the browser
 * (cropImage, canvas) and returns the cropped Blob. In `mode="fractions"` it
 * returns the crop as fractions of the image instead, for a server that crops
 * its own original (the photo inbox crops its master, which has the same
 * aspect as the displayed image); there a tap on the photo centres a small
 * square crop on that point, a Size slider scales the crop around its centre,
 * and the output size is shown. Full screen (phones) the photo takes the
 * height left between the title and the Cancel / Accept footer.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Modal, Button, Group, Slider, Stack, Text } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import ReactCrop, { type Crop, type PercentCrop, type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { cropImage } from './cropImage';
import { cropOutput, type CropFractions } from './cropOutput';

export type { CropFractions } from './cropOutput';

interface CommonProps {
  opened: boolean;
  imageUrl: string;
  onCancel: () => void;
}

interface BlobCropProps extends CommonProps {
  mode?: 'blob';
  onConfirm: (croppedBlob: Blob) => void;
  onSkipCrop?: (originalBlob: Blob) => void;
}

interface FractionCropProps extends CommonProps {
  mode: 'fractions';
  onConfirm: (crop: CropFractions) => void;
  /** Pixel size of the original that gets cropped, for the output size shown. */
  sourceSize?: { width: number; height: number } | null;
  /** The crop chosen before, to adjust. */
  initialCrop?: CropFractions | null;
  /** Fill the screen (phones). */
  fullScreen?: boolean;
}

export type PhotoCropModalProps = BlobCropProps | FractionCropProps;

/** A crop smaller than this on screen (px) is a tap, maybe with a wobble, not a drag. */
const TAP_MAX_PX = 8;
/** A tap's square is this share of the photo's short side. */
const TAP_SQUARE = 1 / 3;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

function toPercent(crop: CropFractions): PercentCrop {
  return { unit: '%', x: crop.x * 100, y: crop.y * 100, width: crop.w * 100, height: crop.h * 100 };
}

function toFractions(crop: PercentCrop): CropFractions {
  const x = clamp(crop.x / 100, 0, 1);
  const y = clamp(crop.y / 100, 0, 1);
  return {
    x,
    y,
    w: clamp(crop.width / 100, 0, 1 - x),
    h: clamp(crop.height / 100, 0, 1 - y),
  };
}

/** Smallest Size (the crop's larger side, % of the photo's). */
const MIN_SIZE = 5;

/** The Size slider's value: the crop's larger side, in % of the photo's. */
function sizeOf(crop: PercentCrop): number {
  return Math.max(crop.width, crop.height);
}

/** The crop scaled to this Size around its centre, kept inside the image. */
function resized(crop: PercentCrop, size: number): PercentCrop {
  const scale = size / sizeOf(crop);
  const width = crop.width * scale;
  const height = crop.height * scale;
  const centreX = crop.x + crop.width / 2;
  const centreY = crop.y + crop.height / 2;
  return {
    unit: '%',
    x: clamp(centreX - width / 2, 0, 100 - width),
    y: clamp(centreY - height / 2, 0, 100 - height),
    width,
    height,
  };
}

/** A square of a third of the short side, centred on the tap and kept inside the image. */
function squareAround(tapX: number, tapY: number, aspect: number): PercentCrop {
  const width = aspect >= 1 ? (TAP_SQUARE * 100) / aspect : TAP_SQUARE * 100;
  const height = aspect >= 1 ? TAP_SQUARE * 100 : TAP_SQUARE * 100 * aspect;
  return {
    unit: '%',
    x: clamp(tapX - width / 2, 0, 100 - width),
    y: clamp(tapY - height / 2, 0, 100 - height),
    width,
    height,
  };
}

export function PhotoCropModal(props: PhotoCropModalProps) {
  const { opened, imageUrl, onCancel } = props;
  const fractionMode = props.mode === 'fractions';
  const [crop, setCrop] = useState<Crop | undefined>(() =>
    props.mode === 'fractions' && props.initialCrop ? toPercent(props.initialCrop) : undefined
  );
  const [isCropping, setIsCropping] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Each opening starts from the crop chosen before (fraction mode).
  const initialCrop = props.mode === 'fractions' ? props.initialCrop : null;
  const latestInitial = useRef(initialCrop);
  latestInitial.current = initialCrop;
  const wasOpened = useRef(opened);
  useEffect(() => {
    if (opened && !wasOpened.current && fractionMode) {
      setCrop(latestInitial.current ? toPercent(latestInitial.current) : undefined);
    }
    wasOpened.current = opened;
  }, [opened, fractionMode]);

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      imgRef.current = e.currentTarget;
      if (fractionMode) return;

      // Set a default centered crop (80% of image)
      const { width, height } = e.currentTarget;
      const cropWidth = width * 0.8;
      const cropHeight = height * 0.8;
      setCrop({
        unit: 'px',
        x: (width - cropWidth) / 2,
        y: (height - cropHeight) / 2,
        width: cropWidth,
        height: cropHeight,
      });
    },
    [fractionMode]
  );

  // Fraction mode keeps the crop in percent, so it doesn't depend on the displayed size.
  const handleChange = useCallback(
    (pixelCrop: PixelCrop, percentCrop: PercentCrop) =>
      setCrop(fractionMode ? percentCrop : pixelCrop),
    [fractionMode]
  );

  const sourceSize = props.mode === 'fractions' ? props.sourceSize : null;

  /** The image's aspect: as displayed, else as loaded, else the original's. */
  const imageAspect = useCallback((): number => {
    const img = imgRef.current;
    if (img?.width && img.height) return img.width / img.height;
    if (img?.naturalWidth && img.naturalHeight) return img.naturalWidth / img.naturalHeight;
    if (sourceSize?.width && sourceSize.height) return sourceSize.width / sourceSize.height;
    return 1;
  }, [sourceSize]);

  // A tap starts a crop of no size where it landed; make it a square around that point.
  const handleComplete = useCallback(
    (pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
      if (!fractionMode) return;
      if (pixelCrop.width >= TAP_MAX_PX || pixelCrop.height >= TAP_MAX_PX) return;
      setCrop(squareAround(percentCrop.x, percentCrop.y, imageAspect()));
    },
    [fractionMode, imageAspect]
  );

  const handleSkipCrop = useCallback(async () => {
    if (props.mode === 'fractions' || !props.onSkipCrop) return;
    const onSkipCrop = props.onSkipCrop;
    setIsCropping(true);
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      onSkipCrop(blob);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Unable to use the full photo. Please try again.',
        color: 'red',
      });
    } finally {
      setIsCropping(false);
    }
  }, [imageUrl, props]);

  const handleConfirm = useCallback(async () => {
    if (!crop?.width || !crop?.height) return;
    if (props.mode === 'fractions') {
      props.onConfirm(toFractions(crop as PercentCrop));
      return;
    }
    if (!imgRef.current) return;

    const onConfirm = props.onConfirm;
    setIsCropping(true);
    try {
      const blob = await cropImage(
        imgRef.current,
        { x: crop.x, y: crop.y, width: crop.width, height: crop.height },
        imgRef.current.width,
        imgRef.current.height
      );
      onConfirm(blob);
    } catch {
      notifications.show({
        title: 'Crop failed',
        message: 'Unable to crop the image. Please try again.',
        color: 'red',
      });
    } finally {
      setIsCropping(false);
    }
  }, [crop, props]);

  const fractions =
    fractionMode && crop?.width && crop.height ? toFractions(crop as PercentCrop) : null;
  const size = fractions && sourceSize ? cropOutput(fractions, sourceSize) : null;
  const fullScreen = props.mode === 'fractions' && !!props.fullScreen;
  const percentCrop = fractions ? (crop as PercentCrop) : null;

  // Full screen, the photo fits the area left for it.
  const { ref: areaRef, width: areaWidth, height: areaHeight } = useElementSize();
  const imageStyle: React.CSSProperties =
    fullScreen && areaWidth > 0 && areaHeight > 0
      ? { maxWidth: areaWidth, maxHeight: areaHeight }
      : { maxWidth: '100%', maxHeight: fullScreen ? 'calc(100dvh - 240px)' : '60vh' };

  const controls = fractionMode ? (
    <Stack gap={6} px={fullScreen ? 'xs' : 0} style={{ flexShrink: 0 }}>
      {percentCrop && (
        <Group gap="sm" wrap="nowrap" align="center">
          <Text size="sm" fw={500}>
            Size
          </Text>
          <Slider
            style={{ flex: 1 }}
            min={MIN_SIZE}
            max={100}
            step={1}
            label={null}
            thumbLabel="Size"
            thumbSize={24}
            value={Math.round(sizeOf(percentCrop))}
            onChange={(value) => setCrop(resized(percentCrop, value))}
          />
        </Group>
      )}
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <Text size="xs" c="gray.7">
          {fractions
            ? 'Drag the crop to move it, its corners or Size to resize, or tap elsewhere to start again.'
            : 'Tap the organism to crop around it, then drag to adjust.'}
        </Text>
        {size && (
          <Text
            size="xs"
            fw={500}
            c={size.low ? 'coral.6' : undefined}
            style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
          >
            {size.label}
          </Text>
        )}
      </Group>
    </Stack>
  ) : null;

  // Full screen, Cancel and Accept share the row; in the organism page's dialog
  // a third button (Use Full Photo) wraps rather than clipping the labels at 360 px.
  const footer = (
    <Group
      justify="flex-end"
      gap="sm"
      wrap={fullScreen ? 'nowrap' : 'wrap'}
      style={fullScreen ? { flexShrink: 0 } : undefined}
    >
      <Button
        variant="default"
        onClick={onCancel}
        disabled={isCropping}
        h={fullScreen ? 44 : undefined}
        style={fullScreen ? { flex: 1 } : undefined}
      >
        Cancel
      </Button>
      {props.mode !== 'fractions' && props.onSkipCrop && (
        <Button
          variant="light"
          onClick={handleSkipCrop}
          loading={isCropping}
          aria-label="Use Full Photo"
        >
          Use Full Photo
        </Button>
      )}
      <Button
        onClick={handleConfirm}
        loading={isCropping}
        disabled={fractionMode && (!fractions || !!size?.tooSmall)}
        aria-label="Accept Crop"
        h={fullScreen ? 44 : undefined}
        style={fullScreen ? { flex: 1 } : undefined}
      >
        Accept Crop
      </Button>
    </Group>
  );

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title={
        <Text fw={600} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Crop Photo
        </Text>
      }
      centered
      size="lg"
      fullScreen={fullScreen}
      closeOnClickOutside={false}
      closeButtonProps={fullScreen ? { size: 44, 'aria-label': 'Close' } : undefined}
      styles={
        fullScreen
          ? {
              content: { display: 'flex', flexDirection: 'column' },
              header: { marginBottom: 0 },
              body: {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--mantine-spacing-sm)',
                padding:
                  'var(--mantine-spacing-xs) var(--mantine-spacing-xs) calc(var(--mantine-spacing-sm) + env(safe-area-inset-bottom, 0px))',
              },
            }
          : undefined
      }
    >
      {fullScreen ? (
        <>
          <Box
            ref={areaRef}
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ReactCrop crop={crop} onChange={handleChange} onComplete={handleComplete}>
              <img src={imageUrl} alt="Crop preview" onLoad={handleImageLoad} style={imageStyle} />
            </ReactCrop>
          </Box>
          {controls}
          {footer}
        </>
      ) : (
        <Stack gap="md">
          <ReactCrop crop={crop} onChange={handleChange} onComplete={handleComplete}>
            <img src={imageUrl} alt="Crop preview" onLoad={handleImageLoad} style={imageStyle} />
          </ReactCrop>
          {controls}
          {footer}
        </Stack>
      )}
    </Modal>
  );
}
